import { motion } from "framer-motion";
import { ArrowUpRight, Check } from "lucide-react";
import { EASE, Overline, Reveal, requestPilot } from "./Reveal";

const TEAM_IMG =
  "https://images.unsplash.com/photo-1758518729685-f88df7890776?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzl8MHwxfHNlYXJjaHwzfHxidXNpbmVzcyUyMHRlYW0lMjBtZWV0aW5nJTIwbW9kZXJuJTIwb2ZmaWNlfGVufDB8fHx8MTc4NzE5ODIyOHww&ixlib=rb-4.1.0&q=85";

const MODELS = [
  {
    num: "01",
    name: "Strategic Pilot",
    tag: "Start here — 60 days",
    featured: true,
    body: "The fastest way to see the delivery model working on your own mandates: 3–5 mandates across 2 functions, structured over 60 days.",
    points: ["60-day structured pilot", "3–5 live mandates", "Agreed KPIs from day one"],
  },
  {
    num: "02",
    name: "Capability Build Programme",
    tag: "Plan · Build · Operate",
    featured: false,
    body: "Enterprise capability hubs from location strategy to full build-operate-transfer, including leadership hiring for new centres.",
    points: ["Location & compliance strategy", "BOT playbooks", "Leadership hiring"],
  },
  {
    num: "03",
    name: "Multi-Year Partnership",
    tag: "All five entities",
    featured: false,
    body: "The full group relationship: all active mandates, a dedicated single point of contact and monthly business reviews against agreed KPIs.",
    points: ["Dedicated SPOC", "Monthly business reviews", "Group-wide priority access"],
  },
];

export default function Engagement() {
  return (
    <section
      id="engage"
      data-testid="engagement-section"
      className="scroll-mt-24 border-y border-white/10 bg-surface/40"
    >
      <div className="mx-auto max-w-7xl px-6 py-24 lg:px-8 lg:py-32">
        <Reveal>
          <Overline>Engagement Models</Overline>
          <div className="mt-6 flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
            <h2 className="max-w-xl font-heading text-4xl font-medium leading-[1.05] tracking-tight sm:text-5xl">
              Three ways to engage — sized to the{" "}
              <span className="text-gold">commitment you want to make first.</span>
            </h2>
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="relative mt-12 h-44 overflow-hidden border border-white/10 sm:h-60">
            <img
              src={TEAM_IMG}
              alt="Dignifyd delivery team in session"
              className="h-full w-full object-cover"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-ink/65" />
            <p className="absolute bottom-4 left-4 max-w-md bg-ink/70 px-4 py-2 font-mono text-[10px] uppercase leading-relaxed tracking-[0.2em] text-white backdrop-blur-md">
              "A 60-day engagement is the fastest way to see the delivery model working
              on your own mandates."
            </p>
          </div>
        </Reveal>

        <div className="mt-14 grid gap-6 lg:grid-cols-3">
          {MODELS.map((m, i) => (
            <motion.article
              key={m.num}
              data-testid={`engagement-card-${i}`}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.9, delay: i * 0.12, ease: EASE }}
              className={`group flex flex-col border p-8 transition-colors duration-500 lg:p-10 ${
                m.featured
                  ? "border-gold/60 bg-gold/[0.06] hover:bg-gold/[0.1]"
                  : "border-white/10 bg-ink hover:border-white/25 hover:bg-surface"
              }`}
            >
              <div className="flex items-center justify-between">
                <p className={`font-mono text-xs tracking-[0.25em] ${m.featured ? "text-gold" : "text-gray-500"}`}>
                  {m.num}
                </p>
                <span
                  className={`px-3 py-1 font-mono text-[10px] uppercase tracking-[0.2em] ${
                    m.featured ? "bg-gold text-ink" : "border border-white/15 text-gray-400"
                  }`}
                >
                  {m.tag}
                </span>
              </div>
              <h3 className="mt-8 font-heading text-2xl font-medium tracking-tight text-white sm:text-3xl">
                {m.name}
              </h3>
              <p className="mt-4 text-sm leading-relaxed text-gray-400">{m.body}</p>
              <ul className="mt-6 flex flex-col gap-3">
                {m.points.map((p, j) => (
                  <li key={p} className="flex items-center gap-3 text-sm text-gray-300">
                    <motion.span
                      initial={{ scale: 0, rotate: -30 }}
                      whileInView={{ scale: 1, rotate: 0 }}
                      viewport={{ once: true, margin: "-40px" }}
                      transition={{ type: "spring", stiffness: 320, damping: 16, delay: 0.3 + j * 0.12 }}
                      className="inline-flex shrink-0"
                    >
                      <Check className="h-4 w-4 text-gold" />
                    </motion.span>
                    {p}
                  </li>
                ))}
              </ul>
              <button
                data-testid={`engagement-cta-${i}`}
                onClick={() => requestPilot(m.featured ? "60-day-pilot" : m.name)}
                className={`mt-10 flex items-center justify-center gap-2 px-6 py-3.5 font-mono text-xs font-medium uppercase tracking-[0.15em] transition-colors duration-300 ${
                  m.featured
                    ? "bg-gold text-ink hover:bg-blue-300"
                    : "border border-white/25 text-white group-hover:border-gold group-hover:text-gold"
                }`}
              >
                {m.featured ? "Book the 60-day pilot" : "Start the conversation"}
                <ArrowUpRight className="h-3.5 w-3.5" />
              </button>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}
